import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Papa from "papaparse";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useLocation, useParams } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import DetailsHeader from "../components/DetailsHeader";
import ForecastCharts from "../components/ForecastCharts";
import ForecastTable, {
  getNormalizedRowsFromDf,
  isEditableColumn,
  isLockedApiRow,
} from "../components/ForecastTable";
import {
  canonicalMonthColumnFromCsvHeader,
  isForecastMonthColumn,
} from "../utils/forecastPeriodColumns";
import { getCompanyId } from "../utils/api";
import { apiRequest } from "../utils/apiRequest";
import {
  ensureProjectWithUsers,
  getProjectById,
  refreshForCurrentRoute,
} from "../utils/sessionStorageLayer";
import {
  SNAPSHOT_COOLDOWN_MS,
  formatCountdown,
  formatRelative,
  formatSnapshotTime,
  getLatestSnapshotAt,
} from "../utils/snapshotTime";
import { showToast } from "../utils/toastOptions";
import "./Forecast.css";

const isMonthCol = isForecastMonthColumn;

function getBudgetCode(row) {
  const code = row?.wbs_code?.flat_code ?? row?.["Budget Code"] ?? row?.budget_code;
  return code != null && code !== "" ? String(code).trim() : null;
}
function isFieldCheckedForChanges(key) {
  return isEditableColumn(key);
}
function valueEqual(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return Math.abs(na - nb) < 1e-6;
  const sa = a == null ? "" : String(a).trim();
  const sb = b == null ? "" : String(b).trim();
  return sa === sb;
}
function rowHasEditableChanges(edited, original) {
  const keys = new Set([...Object.keys(edited), ...Object.keys(original)]);
  for (const key of keys) {
    if (!isFieldCheckedForChanges(key)) continue;
    if (!valueEqual(edited[key], original[key])) return true;
  }
  return false;
}
function getChangedRows(editedRows, originalRows) {
  const byCode = new Map();
  (originalRows || []).forEach((r) => {
    const code = getBudgetCode(r);
    if (code) byCode.set(code, r);
  });
  const changed = [];
  (editedRows || []).forEach((row) => {
    const code = getBudgetCode(row);
    const original = code ? byCode.get(code) : null;
    if (!original) {
      changed.push(row);
      return;
    }
    if (rowHasEditableChanges(row, original)) changed.push(row);
  });
  return changed;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function monthHeaderToPeriodDate(header) {
  const m = (header || "").trim();
  const match = m.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (!match) return null;
  const monthName = match[1];
  const year = match[2];
  const monthIdx = MONTH_NAMES.findIndex((n) => n.toLowerCase() === monthName.toLowerCase());
  if (monthIdx === -1) return null;
  const mm = String(monthIdx + 1).padStart(2, "0");
  return `${year}-${mm}`;
}
function parseFloatSafe(val, def = 0) {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : def;
}
/** Build one row in the shape Procore expects (backend will add budget_line_item_id, wbs_code_id from budget_code). */
function rowToProcoreShape(row) {
  const budget_code = getBudgetCode(row);
  if (!budget_code) return null;

  const rawFtc = row["Forecast To Complete"];
  const forecast_to_complete = rawFtc != null && rawFtc !== "" ? parseFloatSafe(rawFtc, 0) : null;
  const curve = (row.Curve || "").toString().trim().toLowerCase();
  const start = row["Start Date"];
  const start_date =
    start != null && String(start).trim() !== "" ? String(start).trim().slice(0, 10) : undefined;
  const end = row["End Date"];
  const end_date =
    end != null && String(end).trim() !== "" ? String(end).trim().slice(0, 10) : undefined;

  const periods = [];
  Object.keys(row || {}).forEach((key) => {
    if (!isMonthCol(key)) return;
    const period_date = monthHeaderToPeriodDate(key);
    if (!period_date) return;
    const amount = parseFloatSafe(row[key], 0);
    periods.push({ period_date, amount });
  });
  periods.sort((a, b) => a.period_date.localeCompare(b.period_date));

  const out = {
    budget_code,
    periods,
  };
  if (forecast_to_complete != null) out.forecast_to_complete = forecast_to_complete;
  if (curve) out.curve = curve;
  if (start_date) out.start_date = start_date;
  if (end_date) out.end_date = end_date;
  return out;
}

export default function Forecast() {
  const { companyId: companyIdParam, projectId: projectIdParam } = useParams();
  const location = useLocation();
  const companyId = Number(companyIdParam);
  const projectId = Number(projectIdParam);

  const [project, setProject] = useState(null);
  const [projectName, setProjectName] = useState("Unknown Project");

  // Load project from session storage layer (ensure project with users in storage, then read)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureProjectWithUsers(companyId, projectId, false, false);
        if (cancelled) return;
        const foundProject = getProjectById(companyId, projectId);
        if (foundProject) {
          setProject(foundProject);
          setProjectName(foundProject.name ?? "Unknown Project");
        }
      } catch (error) {
        if (!cancelled) showToast("error", error.message || "Failed to load project");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, projectId]);

  const [files, setFiles] = useState([]);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createSnapshot, setCreateSnapshot] = useState(false);
  const [customName, setCustomName] = useState("");
  const [showCharts, setShowCharts] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  const [views, setViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState("Current");

  const [budgetExist, setBudgetExist] = useState(true);
  const [df, setDf] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [showWaitingMessage, setShowWaitingMessage] = useState(false);
  const [waitingDots, setWaitingDots] = useState(0);
  const [editedRows, setEditedRows] = useState([]); // live edits
  const [originalRows, setOriginalRows] = useState([]); // normalized snapshot for change detection
  const [serverRows, setServerRows] = useState([]);
  const [csvRawRows, setCsvRawRows] = useState([]);
  const [csvMatchIssues, setCsvMatchIssues] = useState(null);
  const [lastMatchSignature, setLastMatchSignature] = useState("");

  const latestSnapshotAt = useMemo(() => getLatestSnapshotAt(snapshots), [snapshots]);
  const snapshotRemainingMs = latestSnapshotAt
    ? Math.max(0, latestSnapshotAt.getTime() + SNAPSHOT_COOLDOWN_MS - now)
    : 0;
  const snapshotCoolingDown = snapshotRemainingMs > 0;

  useEffect(() => {
    if (!submitDialogOpen) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [submitDialogOpen]);

  // Track whether a CSV file is actively loaded for preview (prevents loadCurrentForecast from overwriting)
  const csvActiveRef = useRef(false);

  // Ref to hold the latest loadCurrentForecast without causing effect re-fires
  const loadForecastRef = useRef(null);

  // Keep originalRows in sync with df so we can diff edited vs original on save
  useEffect(() => {
    setOriginalRows(getNormalizedRowsFromDf(df));
  }, [df]);

  useEffect(
    () => () => {
      files.forEach((f) => URL.revokeObjectURL(f.preview));
    },
    [files]
  );

  // --- Data loading ---
  const loadCurrentForecast = useCallback(
    async (forceRefresh = false) => {
      if (csvActiveRef.current && !forceRefresh) return;
      if (selectedViewId == null) return;
      setLoadingForecast(true);
      try {
        const base = process.env.REACT_APP_API_BASE_URL;
        if (!companyId) throw new Error("Project missing Company ID");
        const url = `${base}/forecast/companies/${companyId}/projects/${projectId}/views/${selectedViewId}/data/`;
        const json = await apiRequest(url, { method: "GET", forceRefresh });
        if (csvActiveRef.current && !forceRefresh) return;
        setDf(json?.forecast_data || null);
        setServerRows(json?.forecast_data || []);
        setEditedRows([]);
        showToast("success", "Current forecast loaded");
      } catch (err) {
        if (csvActiveRef.current && !forceRefresh) return;
        setDf(null);
        setBudgetExist(false);
        showToast("error", err.message || "Failed to load forecast data");
      } finally {
        setLoadingForecast(false);
      }
    },
    [companyId, projectId, selectedViewId]
  );

  // Keep ref in sync so the initial-load effect always calls the latest version
  loadForecastRef.current = loadCurrentForecast;

  const fetchSnapshots = useCallback(
    async (forceRefresh = false) => {
      if (selectedViewId == null) return;
      try {
        const data = await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/forecast/companies/${companyId}/projects/${projectId}/views/${selectedViewId}/snapshot-list/`,
          { method: "GET", forceRefresh }
        );
        const snaps = data.snapshots || [];
        setSnapshots([{ snapshot_id: "current", snapshot_name: "Current" }, ...snaps]);
        setSelectedSnapshot("Current");
      } catch (error) {
        showToast("error", error.message || "Failed to fetch snapshots");
      }
    },
    [companyId, projectId, selectedViewId]
  );

  const fetchSnapshotData = async (name, forceRefresh = false) => {
    const snap = snapshots.find((s) => s.snapshot_name === name);
    if (!snap || snap.snapshot_id === "current") return;
    setLoadingForecast(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const url = `${base}/forecast/companies/${companyId}/projects/${projectId}/snapshot/${snap.snapshot_id}/data/`;
      const json = await apiRequest(url, { method: "GET", forceRefresh });
      setDf(json?.snapshot?.detail_rows || json?.forecast_data || null);
      setServerRows(json?.snapshot?.detail_rows || json?.forecast_data || []);
      setEditedRows([]); // reset edits when switching snapshots
      showToast("success", `Snapshot "${name}" loaded`);
    } catch (err) {
      showToast("error", err.message || "Failed to load snapshot");
    } finally {
      setLoadingForecast(false);
    }
  };

  const fetchViews = useCallback(async () => {
    try {
      const data = await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/forecast/companies/${companyId}/projects/${projectId}/budget-views/`,
        { method: "GET" }
      );
      const v = data?.views || [];
      setViews(v);
      if (v.length === 0) setBudgetExist(false);
    } catch (error) {
      showToast("error", error.message || "Failed to fetch views");
      setBudgetExist(false);
    }
  }, [companyId, projectId]);

  useEffect(() => {
    if (companyId && projectId) fetchViews();
  }, [companyId, projectId, fetchViews]);

  useEffect(() => {
    if (views.length > 0 && selectedViewId == null) setSelectedViewId(views[0].id);
  }, [views, selectedViewId]);

  useEffect(() => {
    if (companyId && projectId && selectedViewId != null) fetchSnapshots();
  }, [companyId, projectId, selectedViewId, fetchSnapshots]);

  const projectCompanyId = getCompanyId(project);
  useEffect(() => {
    if (projectCompanyId && selectedViewId != null && !csvActiveRef.current) {
      loadForecastRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCompanyId, selectedViewId]);

  // After 5s of loading, show "Waiting for Procore..." with animating dots
  useEffect(() => {
    if (!loadingForecast) {
      setShowWaitingMessage(false);
      return;
    }
    const t = setTimeout(() => setShowWaitingMessage(true), 5000);
    return () => clearTimeout(t);
  }, [loadingForecast]);

  useEffect(() => {
    if (!showWaitingMessage) return;
    const id = setInterval(() => setWaitingDots((d) => (d + 1) % 3), 500);
    return () => clearInterval(id);
  }, [showWaitingMessage]);

  const handleSnapshotChange = async (name) => {
    setSelectedSnapshot(name);
    setFiles([]);
    setCsvRawRows([]);
    setCsvMatchIssues(null);
    setEditedRows([]);
    if (name === "Current") await loadCurrentForecast(true);
    else await fetchSnapshotData(name, true);
  };

  const extractBudgetCode = useCallback((row) => {
    if (!row) return "";
    const code =
      row["Budget Code"] ||
      row["Budget code"] ||
      row["budget code"] ||
      row.budget_code ||
      row.budgetCode ||
      row?.wbs_code?.flat_code ||
      row.flat_code ||
      row.cost_code ||
      "";
    return typeof code === "string" || typeof code === "number" ? String(code).trim() : "";
  }, []);

  const runCsvMatchCheck = useCallback(
    (csvRows = [], procoreRows = []) => {
      const issues = { csvOnly: [], procoreOnly: [], missingBudgetCodeRows: [] };
      if (!csvRows.length || !procoreRows.length) return issues;

      const procoreCodes = new Set();
      const requiredCodes = new Set();
      procoreRows.forEach((row) => {
        const code = extractBudgetCode(row);
        if (!code) return;
        procoreCodes.add(code);
        if (!isLockedApiRow(row)) requiredCodes.add(code);
      });

      const csvCodes = new Set();
      csvRows.forEach((row, idx) => {
        const code = extractBudgetCode(row);
        if (!code) {
          issues.missingBudgetCodeRows.push(idx + 2); // +2 accounts for header row
          return;
        }
        csvCodes.add(code);
        if (!procoreCodes.has(code)) {
          issues.csvOnly.push(code);
        }
      });

      requiredCodes.forEach((code) => {
        if (!csvCodes.has(code)) {
          issues.procoreOnly.push(code);
        }
      });

      // Deduplicate
      issues.csvOnly = Array.from(new Set(issues.csvOnly));
      issues.procoreOnly = Array.from(new Set(issues.procoreOnly));

      return issues;
    },
    [extractBudgetCode]
  );

  useEffect(() => {
    if (csvRawRows.length && serverRows.length) {
      setCsvMatchIssues(runCsvMatchCheck(csvRawRows, serverRows));
    }
  }, [csvRawRows, serverRows, runCsvMatchCheck]);

  useEffect(() => {
    if (!csvMatchIssues || !csvRawRows.length || !serverRows.length) return;
    const signature = `${csvMatchIssues.csvOnly.length}|${csvMatchIssues.procoreOnly.length}|${csvMatchIssues.missingBudgetCodeRows.length}`;
    if (signature === lastMatchSignature) return;
    setLastMatchSignature(signature);
    if (
      csvMatchIssues.csvOnly.length ||
      csvMatchIssues.procoreOnly.length ||
      csvMatchIssues.missingBudgetCodeRows.length
    ) {
      const parts = [];
      if (csvMatchIssues.csvOnly.length)
        parts.push(`${csvMatchIssues.csvOnly.length} CSV codes not in Procore`);
      if (csvMatchIssues.procoreOnly.length)
        parts.push(`${csvMatchIssues.procoreOnly.length} Procore codes missing in CSV`);
      if (csvMatchIssues.missingBudgetCodeRows.length)
        parts.push(`${csvMatchIssues.missingBudgetCodeRows.length} rows missing Budget Code`);
      showToast("error", `CSV validation issues: ${parts.join(" | ")}`);
    } else {
      showToast("success", "CSV rows match current Procore rows");
    }
  }, [csvMatchIssues, csvRawRows.length, serverRows.length, lastMatchSignature]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const mapped = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );
      setSelectedSnapshot("Current");
      setFiles(mapped);
      setCsvRawRows([]);
      setCsvMatchIssues(null);
      setEditedRows([]);
      const file = mapped[0];
      if (file) {
        csvActiveRef.current = true;
        Papa.parse(file, {
          worker: true,
          header: true,
          skipEmptyLines: true,
          complete: ({ data, meta }) => {
            const rows = data || [];
            const columns = meta.fields || [];
            const serverNormalized = getNormalizedRowsFromDf(serverRows);
            const discardedCols = new Set();

            /** Map CSV header to server/expected column name (case-insensitive). */
            function normalizeCsvColumnName(csvCol) {
              const trimmed = (csvCol || "").trim();
              const lower = trimmed.toLowerCase();
              const MAP = {
                "budget code": "Budget Code",
                "budget code description": "description",
                "forecast to complete": "Forecast To Complete",
                "cost type": "category",
                "start date": "Start Date",
                "end date": "End Date",
              };
              if (MAP[lower] !== undefined) return MAP[lower];
              const tier = lower.match(/^cost code tier (\d+)$/);
              if (tier) return `cost_code_level_${tier[1]}`;
              return canonicalMonthColumnFromCsvHeader(csvCol);
            }

            function getBudgetCodeFromCsvRow(csvRow) {
              const code =
                csvRow["Budget Code"] ?? csvRow["Budget code"] ?? csvRow["budget code"] ??
                csvRow["WBS Code"] ?? csvRow["WBS code"] ?? csvRow["wbs code"] ??
                csvRow.flat_code ?? csvRow.budget_code ?? "";
              return code != null && String(code).trim() !== "" ? String(code).trim() : null;
            }

            function normalizeCsvValue(col, val) {
              if (val == null || val === "") return val;
              if (col === "Start Date" || col === "End Date") {
                const d = new Date(val);
                return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
              }
              if (isMonthCol(col)) return Number.isFinite(Number(val)) ? Number(val) : val;
              if (col === "Forecast To Complete" || col === "Curve" || col === "Notes") return val;
              return val;
            }

            const readOnlyMismatches = [];
            const mergedRows = rows.map((csvRow, idx) => {
              const code = getBudgetCodeFromCsvRow(csvRow);
              const serverRow = serverNormalized.find((s) => getBudgetCode(s) === code);
              if (!serverRow) {
                return { id: idx, ...csvRow };
              }
              const merged = { ...serverRow, id: idx };
              columns.forEach((csvCol) => {
                const serverCol = normalizeCsvColumnName(csvCol);
                const csvVal = csvRow[csvCol];
                if (isEditableColumn(serverCol) && !serverRow.locked) {
                  merged[serverCol] = normalizeCsvValue(serverCol, csvVal);
                } else if (isEditableColumn(serverCol)) {
                  merged[serverCol] = serverRow[serverCol];
                } else {
                  const serverVal = serverRow[serverCol];
                  const serverHasThisCol = Object.prototype.hasOwnProperty.call(
                    serverRow,
                    serverCol
                  );
                  if (serverHasThisCol) {
                    const csvHasVal = csvVal != null && String(csvVal).trim() !== "";
                    const skipCategory = serverCol === "category";
                    if (!skipCategory && csvHasVal && !valueEqual(serverVal, csvVal)) {
                      discardedCols.add(serverCol);
                      readOnlyMismatches.push({
                        column: serverCol,
                        budgetCode: code,
                        csvValue: csvVal,
                        serverValue: serverVal,
                      });
                    }
                    merged[serverCol] = serverVal;
                  } else {
                    merged[serverCol] = normalizeCsvValue(serverCol, csvVal);
                  }
                }
              });
              return merged;
            });

            if (readOnlyMismatches.length > 0) {
              showToast(
                "error",
                `Read-only column values must match current data. Discarded: ${[...discardedCols].join(", ")}`
              );
            }

            const allColumns = [...new Set(mergedRows.flatMap((r) => Object.keys(r)))];
            const colOrder = ["id", ...allColumns.filter((c) => c !== "id")];
            const dataArrays = mergedRows.map((r) => colOrder.map((c) => r[c]));
            setCsvRawRows(rows);
            setDf({ $columns: colOrder, $data: dataArrays });

            const csvRowsNormalized = rows.map((row) => {
              const r = {};
              columns.forEach((c) => (r[c] = row[c]));
              return r;
            });
            const issues = runCsvMatchCheck(csvRowsNormalized, serverRows);
            setCsvMatchIssues(issues);
            const signature = `${issues.csvOnly.length}|${issues.procoreOnly.length}|${issues.missingBudgetCodeRows.length}`;
            setLastMatchSignature(signature);

            if (
              issues.csvOnly.length ||
              issues.procoreOnly.length ||
              issues.missingBudgetCodeRows.length
            ) {
              const parts = [];
              if (issues.csvOnly.length)
                parts.push(`${issues.csvOnly.length} CSV codes not in Procore`);
              if (issues.procoreOnly.length)
                parts.push(`${issues.procoreOnly.length} Procore codes missing in CSV`);
              if (issues.missingBudgetCodeRows.length)
                parts.push(`${issues.missingBudgetCodeRows.length} rows missing Budget Code`);
              showToast("error", `CSV validation issues: ${parts.join(" | ")} — CSV discarded`);
              mapped.forEach((f) => {
                if (f.preview) URL.revokeObjectURL(f.preview);
              });
              setFiles([]);
              setCsvRawRows([]);
              setCsvMatchIssues(null);
              csvActiveRef.current = false;
              setDf(serverRows.length ? serverRows : null);
            } else {
              showToast("success", "CSV rows match current Procore rows");
            }
          },
          error: (err) => {
            setCsvRawRows([]);
            setCsvMatchIssues(null);
            csvActiveRef.current = false;
            showToast("error", err.message || "Failed to parse CSV");
          },
        });
      } else {
        csvActiveRef.current = false;
        showToast("warning", "No file accepted — please select a .csv file");
      }
    },
    [runCsvMatchCheck, serverRows]
  );
  const csvUploadEnabled = !loadingForecast && serverRows.length > 0;
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: csvUploadEnabled ? onDrop : () => {},
    multiple: false,
    accept: { "text/csv": [".csv"], "application/vnd.ms-excel": [".csv"], "text/plain": [".csv"] },
    maxSize: 15 * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    noDrag: !csvUploadEnabled,
    disabled: !csvUploadEnabled,
  });

  const removeFile = (index) => {
    URL.revokeObjectURL(files[index].preview);
    setFiles([]);
    setCsvRawRows([]);
    setCsvMatchIssues(null);
    csvActiveRef.current = false;
    setLoadingForecast(true);
    setDf(null);
    setServerRows([]);
    showToast("info", "Removed CSV — refreshing from backend…");
    loadCurrentForecast(true);
  };

  const createSnapshotAPI = async (name) => {
    if (selectedViewId == null) {
      showToast("error", "Select a view first");
      throw new Error("Select a view first");
    }
    try {
      await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/forecast/companies/${companyId}/projects/${projectId}/create-snapshot/`,
        { method: "POST", body: { snapshot_name: name, budget_view_id: selectedViewId } }
      );
    } catch (error) {
      showToast("error", error.message || "Failed to create snapshot");
      throw error;
    }
  };

  const uploadForecastRows = async (forecasting_rows) => {
    if (selectedViewId == null) {
      showToast("error", "Select a view first");
      throw new Error("Select a view first");
    }
    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/forecast/companies/${companyId}/projects/${projectId}/views/${selectedViewId}/upload/`,
        { method: "POST", body: { forecasting_rows } }
      );
      return response;
    } catch (error) {
      showToast("error", error.message || "Failed to upload forecast rows");
      throw error;
    }
  };

  const handleFinalSubmit = async () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const name = createSnapshot ? customName || `Auto_Snapshot_${timestamp}` : null;
    try {
      if (files.length > 0 && csvMatchIssues) {
        const { csvOnly = [], procoreOnly = [], missingBudgetCodeRows = [] } = csvMatchIssues;
        if (csvOnly.length || procoreOnly.length || missingBudgetCodeRows.length) {
          showToast(
            "error",
            "Resolve CSV/Procore mismatches (unmatched or missing Budget Codes) before saving."
          );
          return;
        }
      }
      setSaving(true);
      if (createSnapshot && name && !snapshotCoolingDown) await createSnapshotAPI(name);
      const rowsToSend =
        files.length > 0
          ? getNormalizedRowsFromDf(df)
          : getChangedRows(editedRows || [], originalRows);
      const hasRowsToSend =
        files.length > 0
          ? df && rowsToSend.length > 0
          : editedRows && editedRows.length > 0 && rowsToSend.length > 0;
      if (!hasRowsToSend) {
        if (files.length > 0) {
          showToast("warning", "No table data to save — upload a CSV first.");
        } else if (!editedRows || editedRows.length === 0) {
          showToast("warning", "Nothing to save — upload a CSV or edit the table first.");
        } else {
          showToast("info", "No changes to save");
        }
        return;
      }
      const forecasting_rows = rowsToSend
        .filter((r) => !r.locked)
        .map(rowToProcoreShape)
        .filter(Boolean);
      if (forecasting_rows.length === 0) {
        showToast("error", "No valid rows to save (missing budget code)");
        return;
      }
      const uploadResult = await uploadForecastRows(forecasting_rows);
      const rowErrors = uploadResult?.row_errors || [];
      if (rowErrors.length > 0) {
        showToast(
          "error",
          `Procore rejected ${rowErrors.length} row(s). Check the console for details.`
        );
        console.error("Procore row-level errors:", rowErrors);
      }
      setSubmitDialogOpen(false);
      setCreateSnapshot(false);
      setCustomName("");
      setFiles([]);
      setEditedRows([]);
      csvActiveRef.current = false;
      await loadCurrentForecast(true);
      await fetchSnapshots(true);
      if (rowErrors.length === 0) {
        showToast("success", "Forecast saved successfully");
      }
    } catch (error) {
      showToast("error", error.message || "Error submitting forecast");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page forecast-page">
      <DetailsHeader
        title={`Project: ${projectName}`}
        onBack={() => window.history.back()}
        onRefresh={async () => {
          await refreshForCurrentRoute(location.pathname);
          const p = getProjectById(companyId, projectId);
          if (p) {
            setProject(p);
            setProjectName(p.name ?? "Unknown Project");
          }
          fetchViews();
          fetchSnapshots(true);
          if (selectedSnapshot === "Current") loadCurrentForecast(true);
          else fetchSnapshotData(selectedSnapshot, true);
          showToast("info", "Data refreshed");
        }}
        backTooltip="Go back to previous page"
        refreshTooltip="Refresh snapshots and table data"
      >
        <Box
          {...getRootProps()}
          sx={{
            border: "1px dashed",
            borderColor: isDragActive ? "success.main" : "grey.400",
            borderRadius: 1.5,
            bgcolor: isDragActive ? "#f1f8f2" : "background.paper",
            boxShadow: 1,
            px: 3,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            textAlign: "center",
            transition: "border-color 120ms, background-color 120ms",
            opacity: csvUploadEnabled ? 1 : 0.6,
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Upload Forecast
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Tooltip
              title={
                csvUploadEnabled ? "" : "Load the table first (select a view and wait for data)"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  disabled={!csvUploadEnabled}
                  onClick={open}
                >
                  Import CSV
                </Button>
              </span>
            </Tooltip>
            {files.map((file, idx) => (
              <Chip
                key={file.name + idx}
                icon={<InsertDriveFileOutlinedIcon />}
                label={`${file.name} · ${(file.size / 1024).toFixed(1)} KB`}
                color="success"
                size="small"
                onDelete={() => removeFile(idx)}
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "success.dark",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "success.dark" },
                  "& .MuiChip-deleteIcon": { color: "error.main" },
                  "& .MuiChip-deleteIcon:hover": { color: "error.dark" },
                }}
                deleteIcon={
                  <Tooltip title="Remove CSV and reload from Procore">
                    <DeleteIcon />
                  </Tooltip>
                }
              />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {!csvUploadEnabled
              ? "Load the table first, then upload a CSV (max 15 MB)."
              : isDragActive
                ? "Drop the CSV here…"
                : "Drag & drop a CSV here, or click Import CSV (max 15 MB)."}
          </Typography>
        </Box>
      </DetailsHeader>

      {/* Submit Dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Submit Forecast</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={createSnapshot && !snapshotCoolingDown}
                disabled={snapshotCoolingDown}
                onChange={(e) => setCreateSnapshot(e.target.checked)}
              />
            }
            label="Create snapshot before save"
          />
          {snapshotCoolingDown ? (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "warning.light",
                bgcolor: "#fff8e1",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <AccessTimeIcon sx={{ color: "warning.dark" }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.dark" }}>
                  Next snapshot available in{" "}
                  <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCountdown(snapshotRemainingMs)}
                  </Box>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  color="warning"
                  value={100 - (snapshotRemainingMs / SNAPSHOT_COOLDOWN_MS) * 100}
                  sx={{ my: 0.75, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Last snapshot {formatSnapshotTime(latestSnapshotAt)} · one snapshot per hour
                </Typography>
              </Box>
            </Box>
          ) : (
            latestSnapshotAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                Last snapshot {formatSnapshotTime(latestSnapshotAt)} (
                {formatRelative(latestSnapshotAt, now)})
              </Typography>
            )
          )}
          {createSnapshot && !snapshotCoolingDown && (
            <TextField
              label="Optional Custom Snapshot Name"
              fullWidth
              variant="outlined"
              size="small"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="e.g. April_Forecast_Snapshot"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleFinalSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table + Charts */}
      {(() => {
        if (Array.isArray(df) && df.length === 0) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
              <Typography variant="h6" color="textSecondary">
                No Procore Data
              </Typography>
            </Box>
          );
        }
        if (df) {
          return (
            <>
              <ContentCard elevation={1} p={2} sx={{ mb: 3 }}>
                <ForecastTable
                  df={df}
                  views={views}
                  selectedViewId={selectedViewId}
                  onViewChange={(viewId) => {
                    setSelectedViewId(viewId);
                    setSelectedSnapshot("Current");
                    loadCurrentForecast(true);
                    fetchSnapshots(true);
                  }}
                  snapshots={snapshots}
                  selectedSnapshot={selectedSnapshot}
                  onSnapshotChange={handleSnapshotChange}
                  onOpenSubmit={() => setSubmitDialogOpen(true)}
                  onRowsChange={setEditedRows}
                  showCharts={showCharts}
                  onToggleCharts={setShowCharts}
                />
              </ContentCard>
              {showCharts ? (
                <ContentCard elevation={1} p={2} sx={{ mb: 3 }}>
                  <ForecastCharts df={df} />
                </ContentCard>
              ) : null}
            </>
          );
        }
        return (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
            {budgetExist === false ? (
              <Typography variant="h6" color="textSecondary">
                No Procore Data
              </Typography>
            ) : showWaitingMessage ? (
              <Typography variant="body1" color="text.secondary">
                Waiting for Procore{".".repeat(waitingDots + 1)}
              </Typography>
            ) : (
              <CircularProgress color="inherit" />
            )}
          </Box>
        );
      })()}
    </div>
  );
}
